import React, { useCallback, useState } from 'react'
import { Box, Grid, Stack, TextField, Typography, InputAdornment, IconButton, Tooltip } from '@mui/material';
// icons
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from 'common/customIcons/search';
import StartArrowIIcon from 'common/customIcons/startArrowI';
// utils and helpers
import { debounce } from "utilities/common";
import { getDiscrepancyApprovals } from "../../../redux/slices/hyperwolf/inventoryDistribution";
// custom components
import CommonLayout from 'layouts/DashboardLayout'
import MDButton from 'components/MDButton';
import { Transition } from 'components/distributionSetting/distributionManagement/distributionDetails';
import DiscrepancyApprovalsDetails from 'common/CommonDrawer/hyperwolf/discrepancyApprovalsDetails';
import CommonAgGridTable from "components/tables/CommonAgGridTable";
import StatusText from "common/component/StatusText";
import { DiscrepancyTypeFilter, SubCategoryFilter, BrandFilter, StatusFilter } from "common/filters";

const statusOptions = [
  { label: 'All', value: '' },
  { label: 'Pending', value: 'pending' },
  { label: 'Pending Approval', value: 'pendingApproval' },
  { label: 'Resolved', value: 'resolved' },
  { label: 'Rejected', value: 'rejected' },
]

const DiscrepancyApprovals = () => {
  const [showDetails, setShowDetails] = useState(null)
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshTable, setRefreshTable] = useState(false);
  const [customFilters, setCustomFilters] = useState({});

  const columnDefs = [
    {
      headerName: 'Product Name/SKU',
      field: 'productName',
      suppressMenu: false,
      flex: 2,
      minWidth: 300,
      sortable: false,
      cellRenderer: (params) => {
        if (!params?.data) return "-";
        const { productName, sku, assets } = params?.data || {};
        const { thumbURL, origURL, name } = assets || {}

        return <Box style={{ display: "flex", gap: "10px" }}>
          {(thumbURL || origURL) && <img src={thumbURL || origURL} alt={name} width={40} height={40} />}
          <Box>
            <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px" }}>{productName || "-"}</Typography>
            {sku && <Typography variant='body2' style={{ color: "#9A9AAF", fontSize: "12px" }}>{sku}</Typography>}
          </Box>
        </Box>
      }
    },
    {
      headerName: 'Sub Category',
      field: 'category',
      suppressMenu: false,
      flex: 1.5,
      minWidth: 200,
      sortable: false,
      valueGetter: ({ data }) => data?.category?.name || "-",
    },
    {
      headerName: 'Brand',
      field: 'brand',
      suppressMenu: false,
      flex: 1.25,
      sortable: false,
      valueGetter: ({ data }) => data?.brand?.name || "-",
    },
    {
      headerName: 'Weight',
      field: 'weight',
      suppressMenu: false,
      flex: 1,
      minWidth: 130,
      sortable: false,
      valueGetter: ({ data }) => data?.weight?.toString() ? `${data?.weight}g` : "-",
    },
    {
      headerName: 'Batch',
      field: 'batch',
      suppressMenu: false,
      flex: 1,
      minWidth: 150,
      sortable: false,
      cellRenderer: ({ data }) => {
        const batches = data?.discrepancyBatches || [];
        const firstFive = batches.slice(0, 2);
        const rest = batches.slice(2);

        if (!batches?.length) return "-";

        return (
          <Box>
            {firstFive.map((batch, idx) => (
              <Typography
                key={batch?.batchNo + idx}
                variant='body2'
                style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0", lineHeight: "normal" }}
              >
                {batch?.sku} (Qty {batch?.qty})
              </Typography>
            ))}
            {rest.length > 0 && (
              <Tooltip
                title={
                  <Box>
                    {rest.map((batch, idx) => (
                      <Typography
                        key={batch?.batchNo + idx}
                        variant='body2'
                        style={{ color: "#ffffff", fontWeight: "500", fontSize: "12px", padding: "0", lineHeight: "normal" }}
                      >
                        {batch?.sku} (Qty {batch?.qty})
                      </Typography>
                    ))}
                  </Box>
                }
                placement="top"
                arrow
              >
                <Typography
                  variant='body2'
                  style={{
                    color: "#2E2E3A",
                    fontWeight: "500",
                    fontSize: "12px",
                    padding: "0",
                    cursor: "pointer",
                    textDecoration: "underline",
                    lineHeight: "normal"
                  }}
                >
                  +{rest.length} more
                </Typography>
              </Tooltip>
            )}
          </Box>
        );
      }
    },
    {
      headerName: 'Scanned Qty',
      field: 'scannedQty',
      suppressMenu: false,
      flex: 1.25,
      minWidth: 170,
      sortable: false,
      cellRenderer: ({ data = {} }) => {
        const scanned = data?.scannedQty || 0;
        const expected = data?.expectedQty || 0;
        const percentage = expected ? (scanned / expected) * 100 : 0;

        return (
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <Box
              flex={1}
              height={8}
              borderRadius={4}
              bgcolor="#e0e0e0"
              overflow="hidden"
              mr={1}
            >
              <Box
                height="100%"
                width={`${percentage}%`}
                bgcolor="#4caf50"
                borderRadius={4}
              />
            </Box>
            <Box fontSize="0.8rem" color="#555">
              {scanned}/{expected}
            </Box>
          </Box>
        );
      },
    },
    {
      headerName: 'Discrepancy Type',
      field: 'discrepancyType',
      suppressMenu: false,
      flex: 1.5,
      minWidth: 200,
      sortable: false,
      cellRenderer: ({ data = {} }) => {
        return <Box display="flex" alignItems="center" gap={0.5} className="text-discrepancy">
          <svg xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none">
            <path d="M7.88912 3.12793C9.04623 2.89781 10.2454 3.0163 11.3354 3.46777C12.4255 3.91928 13.3576 4.68403 14.0131 5.66504C14.6684 6.64587 15.018 7.79896 15.018 8.97852C15.0162 10.5601 14.3874 12.077 13.269 13.1953C12.1507 14.3136 10.6337 14.9425 9.0522 14.9443C7.87252 14.9442 6.71961 14.5939 5.73873 13.9385C4.75774 13.283 3.99298 12.3517 3.54146 11.2617C3.09001 10.1718 2.97157 8.97247 3.20162 7.81543C3.43179 6.65829 4.00021 5.59501 4.83443 4.76074C5.66871 3.92646 6.73193 3.35811 7.88912 3.12793ZM9.0522 11.1484C8.89156 11.1485 8.73463 11.1959 8.60103 11.2852C8.46726 11.3745 8.3628 11.5017 8.30123 11.6504C8.2397 11.799 8.22395 11.9624 8.25533 12.1201C8.28671 12.2779 8.36327 12.4233 8.47701 12.5371C8.59078 12.6509 8.7362 12.7284 8.894 12.7598C9.05171 12.7911 9.21516 12.7744 9.36373 12.7129C9.51237 12.6513 9.63958 12.5469 9.72896 12.4131C9.81818 12.2795 9.8656 12.1226 9.86568 11.9619C9.86568 11.7464 9.78065 11.5392 9.62838 11.3867C9.47582 11.2342 9.26795 11.1484 9.0522 11.1484ZM9.0522 5.18262C8.90855 5.18275 8.77098 5.24021 8.66939 5.3418C8.56782 5.44342 8.51029 5.58093 8.51021 5.72461V9.52148C8.51031 9.66515 8.56781 9.80269 8.66939 9.9043C8.77098 10.0059 8.90855 10.0633 9.0522 10.0635C9.19604 10.0635 9.33429 10.006 9.43599 9.9043C9.53757 9.80269 9.59507 9.66515 9.59517 9.52148V5.72461C9.59509 5.58093 9.53757 5.44342 9.43599 5.3418C9.33429 5.24009 9.19604 5.18262 9.0522 5.18262ZM9.40181 1.9375C11.1443 2.02383 12.798 2.75377 14.0376 3.99316C15.1511 5.10676 15.8514 6.55532 16.0454 8.10156C15.8501 6.55608 15.1487 5.10926 14.0356 3.99609C12.7964 2.7569 11.1438 2.02556 9.40181 1.9375Z" fill="#FD4438" />
          </svg>
          <Typography color="#FD4438" fontWeight="600" style={{ fontSize: "12px" }}>{data?.discrepancyType}</Typography>
        </Box>
      },
    },
    {
      headerName: 'Status',
      field: 'status',
      suppressMenu: false,
      flex: 1.25,
      minWidth: 170,
      sortable: false,
      cellRenderer: ({ data }) => <StatusText status={data?.status} />
    },
    {
      headerName: 'Actions',
      field: 'actions',
      suppressMenu: false,
      flex: 1.5,
      minWidth: 240,
      sortable: false,
      cellStyle: { textAlign: 'right', justifyContent: 'end' },
      headerClass: 'right-aligned-cell-header',
      cellRenderer: ({ data = {} }) => {
        const { status, scannedQty, expectedQty } = data || {};
        const isScanning = expectedQty && scannedQty < expectedQty && status !== "resolved" && status !== "rejected" ? true : false;

        return <Box display="flex" alignItems="center" gap={1}>
          {/* {expectedQty && scannedQty < expectedQty && status !== "resolved" && status !== "rejected" ? <MDButton
                        onClick={() => {
                            setShowDetails({ ...data, isScanning: true });
                        }}
                        size='small'
                        startIcon={<StartArrowIIcon fontSize='small' width='14' height='14' />} variant='contained' fontWeight="500" color='primary' className='btn-primary-light'
                        style={{ fontSize: "12px", minHeight: "auto", padding: "6px 12px", fontWeight: "500", whiteSpace: "nowrap" }}
                    >
                        Start Scan
                    </MDButton> : null} */}
          <MDButton
            onClick={() => {
              setShowDetails({ ...data, isScanning });
            }}
            size='small'
            startIcon={isScanning ? <StartArrowIIcon fontSize='small' width='14' height='14' /> : <VisibilityIcon fontSize='small' width='14' height='14' />}
            variant='contained' fontWeight="500" color='primary' className='btn-primary-light'
            style={{ fontSize: "12px", minHeight: "auto", padding: "6px 12px", fontWeight: "500", whiteSpace: "nowrap" }}
          >
            {isScanning ? "Scan & Verify" : status !== "resolved" && status !== "rejected" ? "View & Verify" : "View"}
          </MDButton>
        </Box>

      },
    },
  ];

  const handleDebounceSearch = useCallback(debounce((event) => {
    setCustomFilters(prev => ({ ...prev, search: event.target.value }));
    setRefreshTable(prev => !prev)
  }, 1000), []);

  const handleChange = (e) => {
    const { value, name } = e.target;
    setCustomFilters(prev => ({ ...prev, [name]: value }));
    setRefreshTable(!refreshTable);
  };

  const handleClearFilters = () => {
    setCustomFilters({ search: customFilters.search || "" })
    setRefreshTable(prev => !prev)
  };

  return (
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }}>
        <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <Typography variant='h4' fontWeight="semibold">Discrepancy Approvals</Typography>
          </Grid>

          {/* Search Filter */}
          <Grid item xs={12} sm={8}>
            <Stack direction="row" justifyContent={{ xs: 'start', sm: 'end' }} flexWrap="wrap" alignItems="stretch" gap={2}>
              {/* Search filter */}
              <TextField
                className="custom-search-field"
                placeholder="Search"
                value={searchTerm || ""}
                onChange={e => {
                  const { value } = e.target;
                  setSearchTerm(value);
                  handleDebounceSearch(e);
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">
                    <IconButton size='small'>
                      <SearchIcon width='18px' height='18px' />
                    </IconButton>
                  </InputAdornment>
                }}
                style={{ minWidth: "300px" }}
              />
            </Stack>
          </Grid>
        </Grid>

        {/* Filters */}
        <Box className='cus-filter-sec' mb={2}>
          <Grid className='filter-box' container alignItems='center' justifyContent='flex-start' spacing={1.5}>
            <Grid item xs={12} lg={2.5} xxxl={2} className='p-remove'>
              <DiscrepancyTypeFilter
                value={customFilters?.discrepancyType}
                handleChange={handleChange}
                placeholder={"Discrepancy Type"}
                selectAllLabel={"All"}
              />
            </Grid>
            <Grid item xs={12} lg={2.5} xxxl={2} className='p-remove'>
              <SubCategoryFilter
                value={customFilters?.categoryId}
                handleChange={handleChange}
                placeholder={"Categories"}
                selectAllLabel={"All"}
              />
            </Grid>
            <Grid item xs={12} lg={2.5} xxxl={2}>
              <BrandFilter
                value={customFilters?.brandId || ""}
                handleChange={handleChange}
                placeholder={"Brands"}
                selectAllLabel={"All brands"}
              />
            </Grid>
            <Grid item xs={12} lg={2.5} xxxl={2}>
              <StatusFilter
                value={customFilters?.status || ""}
                handleChange={handleChange}
                placeholder={"Status"}
                selectAllLabel={"All"}
                options={statusOptions}
              />
            </Grid>
            <Grid item xs={12} lg={2} xxxl={2}>
              <MDButton
                sx={{ minWidth: "120px", fontWeight: "normal" }}
                type="button"
                variant="outlined"
                color="dark"
                disabled={!Object.values(customFilters || {})?.filter(Boolean)?.length}
                onClick={handleClearFilters}
              >
                Clear All
              </MDButton>
            </Grid>
          </Grid>
        </Box>

        {/* Listing */}
        <div
          className="ag-theme-quartz table-nodata-size"
          style={{ height: "60vh", width: "100%" }}
        >
          <CommonAgGridTable
            pagination={false}
            columnDefs={columnDefs}
            getDataMethod={getDiscrepancyApprovals}
            refreshTable={refreshTable}
            customFilters={customFilters}
            onCellClicked={(props) => {
              // Prevent row click if click originated from the actions column
              if (props?.column && (["actions"].includes(props.column.getColId()))) {
                return;
              }
              setShowDetails(props?.data);
            }}
          />
        </div>

        {/* Details drawer */}
        {Boolean(showDetails) && <DiscrepancyApprovalsDetails
          open={showDetails}
          TransitionComponent={Transition}
          handleClose={() => setShowDetails(null)}
          setRefreshTable={setRefreshTable}
        />}
      </Box>
    </CommonLayout>
  )
}

export default DiscrepancyApprovals
